<script setup lang="ts">
    import { inject, onMounted, reactive, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import axios from 'axios';
    import moment from 'moment';
    import UserFormComponent from '@/components/UserFormComponent.vue';

    const router = useRouter();
    const route = useRoute();
    const BACKEND_URL = inject('BACKEND_URL');
    const errorMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const startupFormDatas = reactive({
        collectivites: [] as any[]
    });
    const formData = reactive({
        id: null as number | null,
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        fonction: '',
        date_naissance: null as string | null,
        age: 0,
        date_engagement: null as string | null,
        anciennete: 0,
        collectivite_id: null as number | null,
        salaire: '',
        quotite_cessible_pourcent: '',
        quotite_cessible_montant: '',
        photo: null as any,
        photo_preview: '/assets/users_avatar/600w/user.jpg',
    });
    const errors = reactive({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        date_naissance: '',
        age: '',
        date_engagement: '',
        anciennete: '',
        collectivite_id: '',
        salaire: '',
        quotite_cessible_pourcent: '',
        quotite_cessible_montant: '',
    });

    onMounted(() => {
        const form_datas = route.meta.form_datas as { user: any, startupFormDatas: any };
        if (form_datas) {
            if (form_datas.startupFormDatas) {
                startupFormDatas.collectivites = form_datas.startupFormDatas.collectivites || [];
            }
            if (form_datas.user) {
                const user = form_datas.user;
                formData.id = user.id;
                formData.nom = user.nom || '';
                formData.prenom = user.prenom || '';
                formData.telephone = user.telephone || '';
                formData.email = user.email || '';
                formData.fonction = user.fonction || '';
                formData.collectivite_id = user.collectivite_id || null;
                
                // Dates de l'onboarding
                if (user.date_naissance) {
                    const dateNaissance = moment(user.date_naissance);
                    formData.date_naissance = dateNaissance.isValid() ? dateNaissance.format('YYYY-MM-DD') : null;
                    if (dateNaissance.isValid()) {
                        formData.age = moment().diff(dateNaissance, 'years');
                    }
                }
                
                if (user.date_engagement) {
                    const dateEngagement = moment(user.date_engagement);
                    formData.date_engagement = dateEngagement.isValid() ? dateEngagement.format('YYYY-MM-DD') : null;
                    if (dateEngagement.isValid()) {
                        formData.anciennete = moment().diff(dateEngagement, 'years');
                    }
                }
                
                // Informations financières
                if (user.salaire !== null && user.salaire !== undefined) {
                    formData.salaire = String(user.salaire);
                }
                if (user.quotite_cessible_pourcent !== null && user.quotite_cessible_pourcent !== undefined) {
                    formData.quotite_cessible_pourcent = String(user.quotite_cessible_pourcent);
                }
                if (user.quotite_cessible_montant !== null && user.quotite_cessible_montant !== undefined) {
                    formData.quotite_cessible_montant = String(user.quotite_cessible_montant);
                }
                
                if (user.photo_url) {
                    formData.photo_preview = user.photo_url;
                }
            }
        }
    });

    function validatePhone(phoneValue: string) {
        if (!phoneValue) return false;
        const regex = /^\+?[0-9]{8,15}$/;
        return regex.test(phoneValue);
    }

    function validateEmail(emailValue: string) {
        if (!emailValue) return false;
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailValue);
    }

    function validate() {
        let isValid = true;

        errors.nom = '';
        if (!formData.nom || formData.nom.trim() === '') {
            errors.nom = "Le nom est requis";
            isValid = false;
        }

        errors.prenom = '';
        if (!formData.prenom || formData.prenom.trim() === '') {
            errors.prenom = "Le prénom est requis";
            isValid = false;
        }

        errors.date_naissance = '';
        if (!formData.date_naissance) {
            errors.date_naissance = "La date de naissance est requise";
            isValid = false;
        } else {
            const birthDate = moment(formData.date_naissance);
            if (!birthDate.isValid()) {
                errors.date_naissance = "Le format de la date de naissance est invalide";
                isValid = false;
            }
        }

        errors.age = '';
        if (!formData.age || formData.age <= 0) {
            errors.age = "L'âge est requis";
            isValid = false;
        }

        errors.date_engagement = '';
        if (!formData.date_engagement) {
            errors.date_engagement = "La date d'engagement est requise";
            isValid = false;
        } else {
            const engagementDate = moment(formData.date_engagement);
            if (!engagementDate.isValid()) {
                errors.date_engagement = "Le format de la date d'engagement est invalide";
                isValid = false;
            }
        }

        errors.anciennete = '';
        if (formData.anciennete === null || formData.anciennete === undefined || formData.anciennete < 0) {
            errors.anciennete = "L'ancienneté est requise";
            isValid = false;
        }

        errors.telephone = '';
        if (!formData.telephone || formData.telephone.trim() === '') {
            errors.telephone = "Le téléphone est requis";
            isValid = false;
        } else if (!validatePhone(formData.telephone)) {
            errors.telephone = "Le format du téléphone est invalide";
            isValid = false;
        }

        errors.email = '';
        if (!formData.email || formData.email.trim() === '') {
            errors.email = "L'email est requis";
            isValid = false;
        } else if (!validateEmail(formData.email)) {
            errors.email = "Le format de l'email est invalide";
            isValid = false;
        }

        errors.collectivite_id = '';
        if (!formData.collectivite_id) {
            errors.collectivite_id = "La collectivité est requise";
            isValid = false;
        }

        errors.salaire = '';
        if (!formData.salaire || formData.salaire.trim() === '') {
            errors.salaire = "Le salaire est requis";
            isValid = false;
        } else if (isNaN(parseFloat(formData.salaire)) || parseFloat(formData.salaire) <= 0) {
            errors.salaire = "Le format du salaire est invalide";
            isValid = false;
        }

        errors.quotite_cessible_pourcent = '';
        if (!formData.quotite_cessible_pourcent || formData.quotite_cessible_pourcent.trim() === '') {
            errors.quotite_cessible_pourcent = "La quotité cessible en pourcentage est requise";
            isValid = false;
        } else if (isNaN(parseFloat(formData.quotite_cessible_pourcent)) || parseFloat(formData.quotite_cessible_pourcent) <= 0 || parseFloat(formData.quotite_cessible_pourcent) > 100) {
            errors.quotite_cessible_pourcent = "La quotité cessible doit être entre 0 et 100%";
            isValid = false;
        }

        errors.quotite_cessible_montant = '';
        if (!formData.quotite_cessible_montant || formData.quotite_cessible_montant.trim() === '') {
            errors.quotite_cessible_montant = "La quotité cessible en montant est requise";
            isValid = false;
        } else if (isNaN(parseFloat(formData.quotite_cessible_montant)) || parseFloat(formData.quotite_cessible_montant) <= 0) {
            errors.quotite_cessible_montant = "Le format de la quotité cessible en montant est invalide";
            isValid = false;
        }

        isFormValid.value = isValid;
        return isValid;
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = new FormData();
            if (formData.id != null) {
                formToUpload.append("id", `${formData.id}`);
            }
            formToUpload.append("nom", formData.nom);
            formToUpload.append("prenom", formData.prenom);
            formToUpload.append("telephone", formData.telephone);
            formToUpload.append("email", formData.email);
            formToUpload.append("fonction", formData.fonction || '');
            formToUpload.append("collectivite_id", `${formData.collectivite_id}`);
            
            // Champs de l'onboarding
            if (formData.date_naissance) {
                formToUpload.append("date_naissance", formData.date_naissance);
            }
            formToUpload.append("age", `${formData.age}`);
            
            if (formData.date_engagement) {
                formToUpload.append("date_engagement", formData.date_engagement);
            }
            formToUpload.append("anciennete", `${formData.anciennete}`);
            
            formToUpload.append("salaire", formData.salaire);
            formToUpload.append("quotite_cessible_pourcent", formData.quotite_cessible_pourcent);
            formToUpload.append("quotite_cessible_montant", formData.quotite_cessible_montant);

            if (formData.photo?.file) {
                formToUpload.append("photo", formData.photo.file, formData.photo.name);
            }

            await axios.post(`/backoffice/users/save`, formToUpload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            router.push('/app/users/list');
        } catch(error: any) {
            errorMessage.value = error.response?.data?.message || error.message || "Une erreur est survenue lors de l'enregistrement";
            console.error(error);
        } finally {
            loading.value = false;
        }
    }
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-30"></div>
        <section class="s20_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5">Gestion des membres / Edition <span class="text-sm text-grey-500"></span></h4>

                <!-- Utilisation du composant UserFormComponent -->
                <user-form-component
                    :model-value="formData"
                    @update:modelValue="(v) => Object.assign(formData, v)"
                    :collectivites="startupFormDatas.collectivites"
                    :show-collectivite-field="true"
                    :errors="errors"
                    @validate="validate"
                />

                <div class="s10_mobile container-full-2">
                    <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                    <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                    <div class="flex gap-2">
                        <custom-button
                            id="btn-submit"
                            type="button"
                            :classesList="'btn lg w-100'"
                            :loading="loading"
                            @click="onSubmit()"
                        >
                            <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                            <span>Enregistrer</span>
                        </custom-button>
                    </div>
                </div>

                <div class="clear-both h-25"></div>
            </div>
        </section>
    </section>
</template>
